'use client'

import { useEffect, useRef, useState } from 'react'

import { MediaImage as Image } from '@/components/media-image'

interface VideoBackgroundProps {
  src: string
  poster?: string
  className?: string
  /**
   * `sizes` attribute forwarded to the poster `<Image>`. Defaults to
   * `100vw` because most uses are full-bleed heroes. Set explicitly for
   * any usage where the container is narrower than the viewport so Next
   * can pick a smaller responsive variant.
   */
  posterSizes?: string
}

export function VideoBackground({
  src,
  poster,
  className = '',
  posterSizes = '100vw',
}: VideoBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [canPlay, setCanPlay] = useState(false)

  // Defer video element creation until section approaches viewport.
  // Skip entirely on Save-Data or slow connections (2g/slow-2g/3g). The
  // high-quality WebP poster carries the hero on its own.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string }
      }
    ).connection
    if (connection?.saveData) return
    if (connection?.effectiveType && /(^|-)2g$|^3g$/i.test(connection.effectiveType)) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Start playback once video is in the DOM. iOS Safari needs an explicit
  // play() call and may never fire canplaythrough, so we also listen for
  // the playing event as a fallback signal that the video is rendering.
  useEffect(() => {
    if (!shouldLoad) return
    const video = videoRef.current
    if (!video) return

    const show = () => setCanPlay(true)
    video.addEventListener('canplaythrough', show)
    video.addEventListener('playing', show)

    // Explicit play() for iOS Safari
    video.play().catch(() => {})

    return () => {
      video.removeEventListener('canplaythrough', show)
      video.removeEventListener('playing', show)
    }
  }, [shouldLoad])

  return (
    <div ref={containerRef} className={`absolute inset-0 ${className}`} aria-hidden="true">
      {poster && (
        <Image
          src={poster}
          alt=""
          fill
          sizes={posterSizes}
          quality={65}
          priority
          fetchPriority="high"
          aria-hidden="true"
          className={`object-cover transition-opacity duration-1000 ${canPlay ? 'opacity-0' : 'opacity-100'}`}
        />
      )}
      {shouldLoad && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={poster}
          className={`size-full object-cover transition-opacity duration-1000 ${canPlay ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  )
}
